import { authStorage, type AuthUser } from "./auth";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  const refresh = authStorage.getRefresh();
  if (!refresh) return null;
  refreshInFlight = (async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/auth/refresh/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });
      if (!resp.ok) return null;
      const data = (await resp.json()) as { access: string };
      const user = authStorage.getUser();
      if (user) authStorage.setSession(data.access, refresh, user);
      return data.access;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

function logoutAndRedirect(): void {
  authStorage.clear();
  if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
    window.location.href = "/login";
  }
}

async function rawFetch(path: string, init: RequestInit, token: string | null): Promise<Response> {
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return fetch(`${API_BASE}${path}`, { ...init, headers });
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let token = authStorage.getAccess();
  let resp = await rawFetch(path, init, token);

  if (resp.status === 401 && !path.startsWith("/api/auth/")) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      token = newToken;
      resp = await rawFetch(path, init, token);
    }
    if (resp.status === 401) {
      logoutAndRedirect();
      throw new ApiError(401, null, "Sessão expirada. Inicie sessão novamente.");
    }
  }

  const text = await resp.text();
  const body = text ? safeParse(text) : null;

  if (!resp.ok) {
    const detail =
      (body && typeof body === "object" && "detail" in body
        ? String((body as { detail: unknown }).detail)
        : null) ?? `HTTP ${resp.status}`;
    throw new ApiError(resp.status, body, detail);
  }
  return body as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function streamDownload(resp: Response, filename: string): Promise<void> {
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export interface LoginResponse {
  access: string;
  refresh: string;
  user: AuthUser;
  force_password_change: boolean;
}

export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface CaseRow {
  id: number;
  MovNum: number;
  CodProjeto: string;
  MovOp: string;
  NIFDestinatario: string;
  PaisFornecedor: string;
  VATFornecedor: string;
  NomeFornecedor: string;
  TipoDocto: string;
  NumDocto: string;
  DataDocto: string | null;
  ValorTotal: string | null;
  ValorImputado: string | null;
  ValorIncentivo: string | null;
  IVATotal: string | null;
  IVARestituivel: string | null;
  IVAOutrasRestituicoes: string | null;
  IVADedutivelOutros: string | null;
  IVAAutoLiquidado: string | null;
  PagtoData: string | null;
  PagtoRef: string;
  ContratoPublico: boolean;
  IdContratoPublico: string;
  Rubrica: string;
  Descricao: string;
  MovBase: string;
  Estado: "Por Enviar" | "Enviado" | "Por Reenviar";
  candidatura_numero: string;
  created_at: string;
  updated_at: string;
  sent_at: string | null;
  attachments: Array<{ id: number; file: string; original_name: string; uploaded_at: string }>;
  exports: Array<{ id: number; filename: string; created_at: string }>;
}

export interface AiExtractResponse {
  case_id: number;
  fields: {
    VATFornecedor: string;
    NomeFornecedor: string;
    PaisFornecedor: string;
    TipoDocto: string;
    NumDocto: string;
    DataDocto: string;
    ValorTotal: string;
    IVATotal: string;
  };
}

export interface CaseStats {
  total: number;
  por_enviar: number;
  enviado: number;
  por_reenviar: number;
}

export interface ExcelExport {
  id: number;
  filename: string;
  case_count: number;
  generated_by: number | null;
  created_at: string;
  download_url: string | null;
}

export type UserRole = "admin" | "staff" | "user";

export interface UserRow {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  failed_login_attempts: number;
  locked_at: string | null;
  last_login: string | null;
}

export interface UserPayload {
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_active?: boolean;
  is_staff?: boolean;
  is_superuser?: boolean;
  password?: string;
}

export const api = {
  async login(username: string, password: string): Promise<LoginResponse> {
    return request<LoginResponse>("/api/auth/login/", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
  },

  async refresh(refreshToken: string): Promise<{ access: string }> {
    return request<{ access: string }>("/api/auth/refresh/", {
      method: "POST",
      body: JSON.stringify({ refresh: refreshToken }),
    });
  },

  async me(): Promise<AuthUser> {
    return request<AuthUser>("/api/accounts/me/");
  },

  async listCases(params: URLSearchParams = new URLSearchParams()): Promise<Paginated<CaseRow>> {
    const qs = params.toString();
    return request<Paginated<CaseRow>>(`/api/cases/${qs ? `?${qs}` : ""}`);
  },

  async caseStats(): Promise<CaseStats> {
    return request<CaseStats>("/api/cases/stats/");
  },

  async caseTipoOptions(todos = false): Promise<string[]> {
    return request<string[]>(`/api/cases/tipo-options/${todos ? "?todos=1" : ""}`);
  },

  async listExports(): Promise<Paginated<ExcelExport>> {
    return request<Paginated<ExcelExport>>("/api/exports/");
  },

  async generateExport(caseIds?: number[]): Promise<ExcelExport> {
    return request<ExcelExport>("/api/exports/generate/", {
      method: "POST",
      body: JSON.stringify(caseIds ? { case_ids: caseIds } : {}),
    });
  },

  async downloadExport(id: number, filename: string): Promise<void> {
    const token = authStorage.getAccess();
    const resp = await fetch(`${API_BASE}/api/exports/${id}/download/`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (resp.status === 401) {
      const newToken = await refreshAccessToken();
      if (!newToken) {
        logoutAndRedirect();
        throw new ApiError(401, null, "Sessão expirada. Inicie sessão novamente.");
      }
      const retry = await fetch(`${API_BASE}/api/exports/${id}/download/`, {
        headers: { Authorization: `Bearer ${newToken}` },
      });
      if (!retry.ok) {
        throw new ApiError(retry.status, null, "Falha ao descarregar o ficheiro.");
      }
      await streamDownload(retry, filename);
      return;
    }
    if (!resp.ok) {
      throw new ApiError(resp.status, null, "Falha ao descarregar o ficheiro.");
    }
    await streamDownload(resp, filename);
  },

  async aiExtract(caseId: number): Promise<AiExtractResponse> {
    return request<AiExtractResponse>("/api/idp/ai-extract/", {
      method: "POST",
      body: JSON.stringify({ case_id: caseId }),
    });
  },

  async updateCase(id: number, payload: Partial<CaseRow>): Promise<CaseRow> {
    return request<CaseRow>(`/api/cases/${id}/`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },

  async createCase(payload: Partial<CaseRow>): Promise<CaseRow> {
    return request<CaseRow>("/api/cases/", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async getCase(id: number): Promise<CaseRow> {
    return request<CaseRow>(`/api/cases/${id}/`);
  },

  async listUsers(params: URLSearchParams = new URLSearchParams()): Promise<Paginated<UserRow>> {
    const qs = params.toString();
    return request<Paginated<UserRow>>(`/api/accounts/users/${qs ? `?${qs}` : ""}`);
  },

  async createUser(payload: UserPayload): Promise<UserRow> {
    return request<UserRow>("/api/accounts/users/", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async updateUser(id: number, payload: Partial<UserPayload>): Promise<UserRow> {
    return request<UserRow>(`/api/accounts/users/${id}/`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },

  async deleteUser(id: number): Promise<void> {
    return request<void>(`/api/accounts/users/${id}/`, { method: "DELETE" });
  },

  async unlockUser(id: number): Promise<UserRow> {
    return request<UserRow>(`/api/accounts/users/${id}/unlock/`, { method: "POST" });
  },

  async changePassword(newPassword: string): Promise<void> {
    return request<void>("/api/accounts/change-password/", {
      method: "POST",
      body: JSON.stringify({ new_password: newPassword }),
    });
  },

  logout(): void {
    authStorage.clear();
  },
};
